/* istanbul ignore file */
import { singleValue } from "./index";
import { getMissingValue } from "./index";
import { isItemDepartment } from "../utils/deptValidator";

export const getPogCost = (product) => {
  const recommendedQty = product?.pogqty ?? 1;
  const memberCost = product?.pogcost ?? 0;
  return recommendedQty * memberCost;
};

const getAdjQtyBasedOnPackType = (packType, adjQty) => {
  const packValues = packType?.split("F") || [];
  const isFPack = packValues.length === 2;
  const stepper = isFPack ? packValues[1] : 0;
  if (isFPack) {
    if (adjQty % stepper === 0) {
      return adjQty;
    } else {
      return Math.round(adjQty / stepper) * stepper;
    }
  }
  return adjQty;
};

// get adjusted qty based on multiplier, pog qty and missing qty(missingSkus)
export const getAdjustedQty = (
  multiplier = 1,
  pogQty = 0,
  isMissing = false,
  packType = ""
) => {
  if (isMissing) {
    const calculatedAdjQty = pogQty * multiplier;
    const _adjQty = getAdjQtyBasedOnPackType(packType, calculatedAdjQty);
    return _adjQty < 0 ? 0 : _adjQty;
  }
  return 0;
};

export const getTierLevelForQty = (product) => {
  if (
    !Boolean(product?.Tiered) &&
    product?.ZonedMbrCostInd === singleValue.no
  ) {
    return null;
  }
  const qty = !!product?.AdjQty ? parseInt(product?.AdjQty) : 0;
  let tierLevel = 1;
  if (product.Tier1Qty === 0) {
    tierLevel = 2;
  }
  if (isNaN(qty)) {
    return tierLevel;
  }
  if (
    product.Tier2Cost &&
    parseFloat(product.Tier2Cost) !== 0 &&
    qty >= product.Tier2Qty
  ) {
    tierLevel = 2;
  }
  if (
    product.Tier3Cost &&
    parseFloat(product.Tier3Cost) !== 0 &&
    qty >= product.Tier3Qty
  ) {
    tierLevel = 3;
  }
  if (
    product.Tier4Cost &&
    parseFloat(product.Tier4Cost) !== 0 &&
    qty >= product.Tier4Qty
  ) {
    tierLevel = 4;
  }
  return tierLevel;
};

export const getTierBasedCost = (product, tierLevel) => {
  if (!tierLevel) {
    return product?.EachItemCost;
  }
  const tierCostKey = `Tier${tierLevel}Cost`;
  return product[tierCostKey];
};

const discountCalculator = (totalCost, discount) => {
  return totalCost - (totalCost * discount) / 100;
};

// Get updated recommended qty value based on discount multiplier.
const getRecommendedQty = (product) => {
  const pogQty = product?.RecQty || 0;
  const discountMultiplier = product?.DiscMultiplier || 1;
  return pogQty * discountMultiplier;
};

export const getDiscountedAdjCost = (product, tierCost, OneXQty) => {
  const adjQty = product?.AdjQty ?? 0;
  const pogQty = getRecommendedQty(product);
  const discountValue =
    product?.Core === singleValue.yes
      ? product?.CoreDiscount || 0
      : product?.NonCoreDiscount || 0;
  let totalCost = tierCost * adjQty;

  // If adj. qty is more than missing then apply discount to 1x qty only not whole qty.
  if (adjQty > pogQty) {
    totalCost = tierCost * (adjQty - OneXQty);
    return {
      adjCost:
        totalCost + discountCalculator(OneXQty * tierCost, discountValue),
      totalCost: totalCost + OneXQty * tierCost,
    };
  }

  return {
    adjCost: discountCalculator(totalCost, discountValue),
    totalCost: totalCost,
  };
};

// Make sure you pass item with updated/latest multiplier
export const getTierBasedItemValues = (item, itemAdjQty) => {
  //Step 0: Get calculated Adjusted Quantity
  const product = { ...item, AdjQty: itemAdjQty };
  //Step 1: Get Tier Level
  const tier = getTierLevelForQty(product);
  //Step 2: Get Tier Cost Value using tier level (Member Cost)
  const pogCost = getTierBasedCost(product, tier);

  //Step 2.1: get 1x adj. qty and tier cost.
  let OneXQty = 0;
  if (product?.DiscMultiplier) {
    // If item department then rec. qty is equal to adj qty.
    const qty = isItemDepartment(product)
      ? getRecommendedQty(product) || product?.AdjQty || 0
      : getRecommendedQty(product);
    OneXQty = getAdjustedQty(1, qty, getMissingValue(product));
  }

  //Step 3: Get Discounted Cost using tier cost and product adj. qty.
  const { adjCost, totalCost } = getDiscountedAdjCost(
    product,
    pogCost,
    OneXQty
  );

  return { pogCost, adjCost, totalCost, tier };
};
